import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import {
  CONSTANT,
  LoaderService,
  ObservableService,
  OrderService,
} from '@bst-frontend/shared';
import { Subscription } from 'rxjs';

@Component({
  selector: 'bst-frontend-update-fare-modal',
  templateUrl: './update-fare-modal.component.html',
  styleUrls: ['./update-fare-modal.component.css'],
})
export class updateFareModalComponent {
  @Input() orderData: any;
  weightKg = 0;
  @Output() saved = new EventEmitter<any>();
  private subscriptions: Subscription[] = [];

  form!: FormGroup;
  constructor(
    private activeModal: NgbActiveModal,
    private fb: FormBuilder,
    private orderService: OrderService,
    private loader: LoaderService,
    private observableService: ObservableService
  ) {}

  ngOnInit(): void {
    this.weightKg = this.orderData.totals.totalWeight;
    this.buildForm();
    this.populateFromOrderData();
    // subscribe to each extra charge's changes
    this.subscribeToExtraCharges();
  }

  private buildForm() {
    this.form = this.fb.group({
      extraCharges: this.fb.array([]),
    });
  }

  private populateFromOrderData() {
    if (!this.orderData) return;
    const otherCharges = Array.isArray(this.orderData?.otherCharges)
      ? this.orderData.otherCharges
      : [];

    otherCharges.forEach((ch: any, index: number) => {
      const label = String(ch.label || '').trim();
      const chargeVal = Number(ch.charge) || 0;
      const id = ch._id || null;
      if (!ch?.isDiscount && ch?.isExtraFare) {
        this.extraCharges.push(this.buildExtraCharge(label, chargeVal, id));
      }
    });
  }

  buildExtraCharge(name = '', amount = 0, id?: string) {
    return this.fb.group({
      _id: [id || null],
      name: [name, Validators.required],
      amount: [amount, [Validators.required, Validators.min(0)]],
    });
  }

  get extraCharges(): FormArray {
    return this.form.get('extraCharges') as FormArray;
  }

  addExtraCharge() {
    this.extraCharges.push(this.buildExtraCharge());
  }

  removeExtraCharge(index: number) {
    this.extraCharges.removeAt(index);
  }

  // computed values for template convenience
  get estimateAmount(): number {
    const rate = Number(this.form.get('perKgRate')?.value) || 0;
    return this.weightKg * rate;
  }

  controlInvalid(control: AbstractControl | null): boolean {
    return !!(control && control.invalid && (control.touched || control.dirty));
  }

  getExtraControl(idx: number, name: 'name' | 'amount'): AbstractControl {
    return (this.extraCharges.at(idx) as FormGroup).get(
      name
    ) as AbstractControl;
  }

  trackByIndex(index: number): number {
    return index;
  }

  async save() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    // Build "otherCharges" exactly as required
    const otherCharges: any = [];

    const totals = this.orderData?.totals || {};
    totals.amount = totals.productAmount;
    // 3. Extra Charges (dynamic rows)
    this.extraCharges.controls.forEach((ctrl) => {
      const name = ctrl.get('name')?.value;
      const amount = Number(ctrl.get('amount')?.value || 0);
      if (name && amount > 0) {
        totals.amount = totals.amount + amount;
        otherCharges.push({
          label: name,
          charge: amount,
          isExtraFare: true,
        });
      }
    });

    this.orderData?.otherCharges.forEach((ch: any) => {
      totals.amount = totals.amount + (ch?.isDiscount ? -ch.charge : ch.charge);
      if (ch?.isDiscount || !ch?.isExtraFare) {
        otherCharges.push({ ...ch });
      }
    });

    // Final payload:
    const payload = {
      estimate: this.form.get('perKgRate')?.value,
      otherCharges,
      totals,
    };
    this.loader.show();
    try {
      await this.orderService
        .updateOrder(this.orderData._id, payload)
        .toPromise();
      this.observableService.send({ refresh: true });
      // this.loader.hide();
    } catch (error) {
      console.log('Error', error);
    }

    // this.saved.emit(payload);
    this.activeModal.close({ status: 'saved' });
  }

  cancel() {
    this.activeModal.dismiss('cancel');
  }

  private subscribeToExtraCharges() {
    this.subscriptions = this.subscriptions.filter((s) => {
      s.unsubscribe();
      return false;
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
    this.subscriptions = [];
  }
}
